import React, { useState } from 'react';
import { Linkedin, Send } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('');
  const [canSubmit, setCanSubmit] = useState(true);
  const [isSending, setIsSending] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit || isSending) {
      setStatus('Please wait before sending another message.');
      return;
    }
    setIsSending(true);
    setStatus('');
    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        { from_name: formData.name, from_email: formData.email, message: formData.message },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        setStatus('Message sent successfully!');
        setFormData({ name: '', email: '', message: '' });
        setCanSubmit(false);
        setTimeout(() => setCanSubmit(true), 120000);
      })
      .catch((error) => {
        setStatus('Failed to send message. Please try again.');
        console.error(error);
      })
      .finally(() => setIsSending(false));
  };

  const inputClass =
    'w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-100 placeholder-slate-400 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500/50 transition-all duration-300';

  return (
    <section className="bg-slate-900 min-h-screen py-24 px-6">
      <SEO
        title="Kontakt"
        description="Skontaktuj się z Kajetanem Jędrychem — programistą i twórcą stron internetowych. Napisz wiadomość lub połącz się na LinkedIn."
        path="/contact"
      />
      {/* Subtle mesh background */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 10% 20%, rgba(124,58,237,0.06) 0%, transparent 60%), radial-gradient(ellipse 50% 40% at 90% 80%, rgba(6,182,212,0.05) 0%, transparent 60%)',
        }}
      />

      <div className="relative max-w-5xl mx-auto">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-violet-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">
            Contact
          </p>
          <h1 className="text-4xl font-bold text-slate-100 mb-4">Get in Touch</h1>
          <div className="w-16 h-px bg-gradient-to-r from-violet-500 to-cyan-400 mx-auto" />
        </motion.div>

        <div className="flex flex-col md:flex-row items-start gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full md:w-1/2"
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                required
                className={inputClass}
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                required
                className={inputClass}
              />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Your Message"
                required
                rows="5"
                className={inputClass}
              />

              <button
                type="submit"
                disabled={isSending || !canSubmit}
                aria-busy={isSending}
                className="group w-full inline-flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-500 text-white px-6 py-3 rounded-full font-medium transition-all duration-300 hover:shadow-lg hover:shadow-violet-500/25 hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
              >
                {isSending ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-300" />
                    Send Message
                  </>
                )}
              </button>
            </form>

            {status && (
              <p
                className={`mt-4 text-center text-sm font-medium ${
                  status.includes('success') ? 'text-cyan-400' : 'text-red-400'
                }`}
              >
                {status}
              </p>
            )}
          </motion.div>

          {/* LinkedIn / Connect panel */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="w-full md:w-1/2 flex flex-col items-center"
          >
            <a
              href="https://www.linkedin.com/in/kajedrych/"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative mb-6"
            >
              <div className="absolute -inset-2 bg-gradient-to-br from-violet-500/30 to-cyan-500/30 rounded-full opacity-0 group-hover:opacity-100 blur transition-opacity duration-300" />
              <div className="relative bg-slate-800 border border-slate-700 group-hover:border-violet-500/50 p-5 rounded-full transition-all duration-300">
                <Linkedin className="w-12 h-12 text-slate-400 group-hover:text-violet-400 transform group-hover:scale-110 transition-all duration-300" />
              </div>
            </a>

            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-100 mb-2">Let's Connect</h3>
              <p className="text-slate-400 max-w-sm leading-relaxed">
                Feel free to reach out on LinkedIn. I'm always open to discussing new projects,
                opportunities, or just having a professional chat.
              </p>
            </div>

            {/* Info card */}
            <div className="mt-8 w-full bg-slate-800 border border-slate-700 rounded-xl p-6">
              <div className="space-y-3 text-sm text-slate-400">
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0" />
                  Usually responds within 24 hours
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-violet-400 flex-shrink-0" />
                  Open to freelance and full-time opportunities
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0" />
                  Available for consulting on AI testing and QA
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactPage;
